import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { appSettings } from '@visalex/configs';
import { DayPicker, SimpleDropdown } from '@visalex/core';
import { DayPickerInputWrapper } from '@visalex/core/ui';
import { DayPickerSingleProps, useInput, UseInputOptions } from 'react-day-picker';

import { Input, InputProps } from '../../../Input/Input';
import { useCombineInputValue } from '../../lib/useCombineInputValue';
import { useControlledMonth } from '../../lib/useControlledMonth';

import styles from './SingleDayPickerInput.module.scss';

export type SingleDayPickerInputProps = Omit<
  DayPickerSingleProps &
    UseInputOptions & {
      name: string;
    },
  'mode'
> & {
  /** Is dayPicker disabled */
  isDisabled?: boolean;
  enablePortal?: boolean;
  isRHFConnected?: boolean;

  value?: Date;
  inputClassName?: string;

  /** Basic usage is to connect to RHF */
  onChange?(value: Date | null): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onDayPickerHide?(): void;

  /** Align simple dropdown at the end  */
  alignEnd?: boolean;
} & Pick<InputProps, 'valid' | 'postfixContent' | 'prefixContent' | 'prefixClassName' | 'postfixClassName' | 'size'>;

export const SingleDayPickerInput = forwardRef<HTMLInputElement, SingleDayPickerInputProps>(
  (
    {
      month,
      onMonthChange,
      onChange,
      onBlur,
      value: valueFromRHF,
      valid,
      postfixContent,
      prefixContent,
      postfixClassName,
      prefixClassName,
      isDisabled,
      enablePortal = false,
      inputClassName,
      size,
      format = appSettings.dateFormat,
      onDayPickerHide,
      alignEnd = false,
      isRHFConnected,
      defaultSelected,
      ...props
    },
    ref
  ) => {
    const portaledDayPickerRef = useRef<HTMLDivElement>(null);

    const { inputProps, dayPickerProps } = useInput({
      ...props,
      format,
    });

    const [isDayPickerOpen, setIsDayPickerOpen] = useState(false);
    const onDayPickerOpen = () => setIsDayPickerOpen(true);
    const onDayPickerClose = useCallback(() => {
      setIsDayPickerOpen(false);
      /** onDayPickerHide should be called after onChange  */
      setTimeout(() => {
        onDayPickerHide?.();
      });
    }, []);

    const [selectedMonth, setSelectedMonth] = useControlledMonth({
      isDayPickerOpen,
      defaultMonth: month,
      selectedDate: dayPickerProps.selected,
    });

    const value = useCombineInputValue({
      valueFromRHF,
      defaultValue: defaultSelected,
      inputPropsValue: inputProps.value as string,
      formatStr: format,
      isRHFConnected,
    });

    useEffect(() => {
      if (onChange) {
        /** Connect inner state of react-day-picker with RHF and simple onChange */
        onChange(dayPickerProps.selected || null);
      }
    }, [dayPickerProps.selected]);

    return (
      <DayPickerInputWrapper
        isOpen={isDayPickerOpen}
        onDayPickerClose={onDayPickerClose}
        portaledElementRef={portaledDayPickerRef}
      >
        <Input
          name={props.name}
          {...inputProps}
          size={size}
          value={value as string}
          valid={valid}
          postfixContent={postfixContent}
          postfixClassName={postfixClassName}
          prefixContent={prefixContent}
          prefixClassName={prefixClassName}
          className={inputClassName}
          onFocus={(e) => {
            onDayPickerOpen();
            inputProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            inputProps.onBlur?.(e);
            onBlur?.(e);
          }}
          onChange={(_, e) => {
            inputProps.onChange?.(e);
          }}
          disabled={isDisabled}
          autoComplete="off"
          ref={ref}
        />
        {!isDisabled && (
          <SimpleDropdown
            enablePortal={enablePortal}
            show={isDayPickerOpen}
            alignEnd={alignEnd}
            rootNode="#modal-root"
            menuClassName={styles.menu}
            disableAnimation
            menuDataTestId={props.name}
          >
            <DayPicker
              mode="single"
              fixedWeeks
              showOutsideDays
              {...props}
              {...dayPickerProps}
              onMonthChange={(changedMonth) => {
                onMonthChange?.(changedMonth);
                setSelectedMonth(changedMonth);
              }}
              month={selectedMonth}
              className={props.className}
              onSelect={(day, selectedDay, activeModifiers, e) => {
                props.onSelect?.(day, selectedDay, activeModifiers, e);

                onDayPickerClose();
              }}
              ref={portaledDayPickerRef}
            />
          </SimpleDropdown>
        )}
      </DayPickerInputWrapper>
    );
  }
);

SingleDayPickerInput.displayName = 'DatePicker';
